<template lang="pug">
div
	template(v-if="location.isActive")
		title-with-reviews(:title="$t('page.resorts._resort.location:heading_{location}_{resort}', { resort: location.resort.name, location: location.name })" :reviews="location.reviews")
	template(v-else)
		h1 {{$t('page.resorts._resort.location:heading_{location}_{resort}', { resort: location.resort.name, location: location.name })}}
		v-alert(type="warning") Deze locatie is gesloten

	template
		v-row
			v-col
				client-only
					mapbox(
						ref="map"
						style="height:250px"
						:layers="layers"
						:sources="sources"
						:fitbounds="markers.features.map(e=>e.geometry.coordinates)"
						:mapOptions="{zoom: 14, center: this.mapCenter, scrollZoom: false}")
						div(slot="popup")
							h3 {{popupData.name}}
			v-col
				template(v-if="location.url || location.address")
					h2 {{$t('page.resorts._resort.location:contact.info')}}
					template(v-if="location.url")
						h3 {{$t('page.resorts._resort.location:website')}}
						a(:href="location.url" target="_blank" rel="nofollow") {{location.url}}
					template(v-if="location.address")
						h3 {{$t('page.resorts._resort.location:address')}}
						| {{location.address}}
						| {{location.postcode}} {{location.resort.name}}
				v-row
					v-col(v-for="(photo, index) in locationPhotos" :key="photo.id" cols="6")
						v-img(:src="`${photo.thumb}`" :aspect-ratio="16/9" :alt="photo.alt"  @click="openGallery(index)")
			client-only
				lightbox(:media="locationPhotos" :showLightBox="false" :nThumbs="3" ref="lightbox")

	template(v-if="location.isActive")
		v-divider.my-6
		h2 {{$t('page.resorts._resort.location:heading.review_{location}', {location: location.name }) }}
		add-rating.mb-6(v-bind="{type:'location', id: location.id}" @rated="rated")
		add-review-dialog(v-bind="{type:'location', id: location.id, destination: {name: location.name}}" :initial-rating="rating" v-model="showReviewDialog" @reviewed="reviewed")

	template(v-if="location.reviews.nodes.length" )
		h2.mt-3 {{$t('page.resorts._resort.location:review_by_users.heading')}}
		review-list(:items="location.reviews.nodes")

	template(v-if="['nl-NL', 'nl-BE'].includes($i18n.locale)")
		h2.mb-4 {{$t('page.resorts._resort.apresski:checklist.heading_{resort}', { resort: location.resort.name })}}
		ws-checklist(:resort-id="location.resort.id")
</template>

<script>
import gql from 'graphql-tag';
import TitleWithReviews from '@dengl/common/components/title-with-reviews';
import { ref } from '@vue/composition-api';
import AddRating from '@/components/add-rating';
import AddReviewDialog from '@/components/add-review-dialog';
import ReviewList from '@/components/review-list.vue';
import UserContentDetails from '@/components/user-content/fragments.js';
import MetaFragment from '@/components/graphql/metaFragment.graphql';
import useMap from '@/use/map';

const resortLocationDetail = gql`
	query resortLocationDetail($slug: String!, $resort: String!) {
		location: locationWithSlug(slug: $slug, resort: $resort) {
			id
			name
			slug
			url
			address
			postcode
			properties
			isActive
			...MetaFragment
			reviews {
				nodes {
					id
					...UserContentDetails
				}
				average
				voteCount
			}
			photos {
				url
				alt
				caption
			}
			geography {
				location {
					type
					coordinates
				}
			}
			resort {
				id
				name
				slug
				area {
					id
				}
				country {
					id
					name
					countryCode
					slug
				}
				region {
					id
					name
					slug
				}
				area {
					id
					name
					slug
				}
			}
		}
	}
	${UserContentDetails}
	${MetaFragment}
`;

export default {
	nuxtI18n: {
		paths: {
			'nl-NL': '/dorpen/:resort/locatie/:location',
			'de-DE': '/skiorte/:resort/lokalitaet/:location',
			'nl-BE': '/dorpen/:resort/locatie/:location'
		}
	},
	components: {
		AddRating,
		AddReviewDialog,
		ReviewList,
		TitleWithReviews,
		Mapbox: () => (process.client ? import('@/components/mapbox.vue') : null)
	},
	layout: 'default',
	setup() {
		const mapItems = ref([]);
		const { markers, sources, attachListeners, popupData } = useMap(mapItems);
		const layers = [
			{
				id: 'markers',
				type: 'symbol',
				source: 'geojson-markers',
				layout: {
					'icon-image': ['get', 'icon'],
					'icon-size': 0.3,
					'icon-anchor': 'bottom',
					'icon-allow-overlap': true
				},
				minzoom: 0,
				maxzoom: 24
			}
		];
		return {
			mapItems,
			sources,
			layers,
			markers,
			attachListeners,
			popupData
		};
	},
	asyncData({ params, app }) {
		return app.$graphClient
			.query({
				query: resortLocationDetail,
				variables: {
					slug: params.location,
					resort: params.resort
				}
			})
			.then(({ data: { location } }) => {
				let breadcrumbLocationType, breadcrumbLocationRoute, locationCrumbs;

				if (
					location.properties.includes('restaurant') ||
					location.properties.includes('pisterestaurant')
				) {
					breadcrumbLocationType =
						'page.resorts._resort.restaurants:breadcrumb';
					breadcrumbLocationRoute = 'resorts-resort-restaurants';
				} else if (
					location.properties.includes('apresski') ||
					location.properties.includes('club')
				) {
					breadcrumbLocationType = 'page.resorts._resort.apresski:breadcrumb';
					breadcrumbLocationRoute = 'resorts-resort-apresski';
				} else if (location.properties.includes('rental')) {
					breadcrumbLocationType = 'page.resorts._resort.rental:breadcrumb';
					breadcrumbLocationRoute = 'resorts-resort-rental';
				}

				if (breadcrumbLocationType && breadcrumbLocationRoute) {
					locationCrumbs = {
						[app.i18n.t(breadcrumbLocationType)]: {
							name: breadcrumbLocationRoute,
							params: { resort: location.resort.slug }
						},
						[location.name]: {
							name: 'resorts-resort-location',
							params: { resort: location.resort.slug, location: location.slug }
						}
					};
				} else {
					locationCrumbs = {
						[location.name]: {
							name: 'resorts-resort-location',
							params: { resort: location.resort.slug, location: location.slug }
						}
					};
				}

				app.meta(location.meta);
				app.ads({
					sidebar: {
						visible: true,
						targets: {
							Village: location.resort.id,
							Skiresort: location.resort.area.id,
							State: location.resort.region.id,
							Country: location.resort.country.id
						}
					}
				});
				app.resortBreadcrumbs(location.resort, locationCrumbs);

				return { location };
			})
			.catch(error => {
				if (error.message.includes('GraphQL error: Location not found')) {
					return app.httpError(404, null, 'resorts-resort-location-02');
				}
				return app.httpError(500, null, 'resorts-resort-location-01', error);
			});
	},
	data() {
		return { showReviewDialog: false, rating: 0 };
	},
	head() {
		return {
			...this.$structuredData()
		};
	},
	computed: {
		reviewTotal() {
			return this.location.reviews ? this.location.reviews.voteCount : 0;
		},
		locationPhotos() {
			const photos = this.location.photos.slice(0, 3).map(photo => {
				return {
					thumb: `${photo.url}?width=320`,
					src: `${photo.url}?width=1280`,
					caption: `${photo.caption}`,
					alt: `${photo.alt}`
				};
			});
			return photos;
		},
		mapCenter() {
			return this.location.geography.location.coordinates;
		}
	},
	mounted() {
		const newLocation = this.$lodash.cloneDeep(this.location);
		const marker = newLocation.properties[0];
		newLocation.icon = `${
			marker === 'pisterestaurant' ? 'restaurant' : marker
		}-marker`;
		this.mapItems = [newLocation];
	},
	methods: {
		openGallery(index) {
			this.$refs.lightbox.showImage(index);
		},
		reviewed(review) {
			this.location.reviews.nodes.unshift(review);
		},
		rated(rating) {
			this.rating = rating;
			this.showReviewDialog = true;
		}
	}
};
</script>
